import dayjs from 'dayjs'
import {
  doctorProfileForList,
  UserType,
} from '../../../../../models/graphqlTypes'
import React from 'react'
import {
  Box,
  Typography,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Checkbox,
  Stack,
} from '@mui/material'
import { RegisterOptions, useFormContext } from 'react-hook-form'
import { ValidEmailPattern } from '../../../../../utils/isValidEmail'
import { ValidPhonePattern } from '../../../../../utils/isValidPhone'
import { useTranslation } from '../../../../../common/hooks/helper/useTranslation'
import { useUserType } from '../../../../../common/hooks'
import { DoctorSelector } from '../../../../../common/components/selectors/DoctorSelector'
import { useUserProfile } from '../../../../../common/hooks/useUserProfile'
import {
  BoxWithLabel,
  DatePickerController,
  GenderFieldsController,
  MeasurementFieldController,
  TextfieldController,
} from '../../../../../common/components'

export type NewPatientForm = {
  tajNumber: string
  birthDate: dayjs.Dayjs | null
  birthPlace: string
  gender: string
  title?: string
  lastName: string
  firstName: string
  mothersLastName: string
  mothersFirstName: string
  height?: string
  weight?: string
  email: string
  phone: string
  patientSigned?: boolean
  emergencyContactFirstName: string
  emergencyContactLastName: string
  emergencyContactPhone: string
  infoRecipientFirstName: string
  infoRecipientLastName: string
  infoRecipientPhone: string
  assistantOwnDoctors?: doctorProfileForList[]
}

interface Props {
  patientHasProfile: boolean
  invalidEmailValues: string[]
}

const NameFieldsRow = ({
  lastNameField,
  firstNameField,
  lastLabel,
  firstLabel,
  rules,
  flexDirection = 'column',
}: {
  lastNameField: string
  firstNameField: string
  lastLabel: string
  firstLabel: string
  rules?: RegisterOptions
  flexDirection?: 'row' | 'column'
}) => {
  const { t } = useTranslation()
  return (
    <Stack direction={flexDirection} gap={flexDirection === 'column' ? 0 : 2}>
      <TextfieldController
        name={lastNameField}
        label={t(lastLabel)}
        rules={rules}
      />
      <TextfieldController
        name={firstNameField}
        label={t(firstLabel)}
        rules={rules}
      />
    </Stack>
  )
}

export const AddPatientForm: React.FC<Props> = ({
  patientHasProfile,
  invalidEmailValues,
}) => {
  const { t } = useTranslation()
  const profile = useUserProfile()
  const userType = useUserType()

  const {
    formState: { errors },
    register,
  } = useFormContext()

  const required = {
    value: true,
    message: t('messages:warnings.required'),
  }

  return (
    <>
      {userType === UserType.Assistant && (
        <Box mt={2}>
          <DoctorSelector
            label={t('patients:addToDoctors')}
            name="assistantOwnDoctors"
            doctorListFilterData={{ assistantId: profile?.id ?? '' }}
            required
          />
        </Box>
      )}
      <Typography variant="body2" fontWeight="700" mt={2}>
        {t('patients:personalInformation')}
      </Typography>
      <DatePickerController
        name="birthDate"
        label={t('patients:birthdate')}
        rules={{ required }}
        placeholder={t('common:datePlaceholder')}
      />
      <TextfieldController
        name="birthPlace"
        label={t('patients:birthplace')}
        rules={{ required }}
      />

      <GenderFieldsController
        label={t('patients:gender')}
        rules={{ required }}
      />

      <TextfieldController name="title" label={t('patients:title')} />

      <NameFieldsRow
        firstLabel={t('patients:firstName')}
        firstNameField="firstName"
        lastLabel={t('patients:lastName')}
        lastNameField="lastName"
        rules={{ required }}
      />
      <NameFieldsRow
        firstLabel={t('patients:mothersFirstName')}
        firstNameField="mothersFirstName"
        lastLabel={t('patients:mothersLastName')}
        lastNameField="mothersLastName"
        rules={{ required }}
      />

      <Stack direction="row" gap={2}>
        {['height', 'weight'].map((field) => (
          <BoxWithLabel key={field} label={t(`patients:${field}`)}>
            <MeasurementFieldController name={field} />
          </BoxWithLabel>
        ))}
      </Stack>
      <Typography variant="body2" fontWeight="700" mt={2}>
        {t('patients:contact')}
      </Typography>
      <TextfieldController
        name="email"
        label={t('patients:email')}
        rules={{
          required: !patientHasProfile,
          validate: (value) => !invalidEmailValues.includes(value),
          pattern: patientHasProfile
            ? undefined
            : {
                value: ValidEmailPattern,
                message: t('messages:warnings.notEmail'),
              },
        }}
      />
      <TextfieldController
        name="phone"
        label={t('patients:phone')}
        rules={{
          required,
          pattern: {
            value: ValidPhonePattern,
            message: t('messages:warnings.notPhone'),
          },
        }}
      />
      <Typography variant="body2" fontWeight="700" mt={2}>
        {t('patients:emergencyContact')}
      </Typography>
      <NameFieldsRow
        firstLabel={t('patients:contactFirstName')}
        firstNameField="emergencyContactFirstName"
        lastLabel={t('patients:contactLastName')}
        lastNameField="emergencyContactLastName"
        flexDirection="row"
      />
      <TextfieldController
        name="emergencyContactPhone"
        label={t('patients:contactPhone')}
      />

      <Typography variant="body2" fontWeight="700" mt={2}>
        {t('patients:infoRecipient')}
      </Typography>

      <NameFieldsRow
        firstLabel={t('patients:contactFirstName')}
        firstNameField="infoRecipientFirstName"
        lastLabel={t('patients:contactLastName')}
        lastNameField="infoRecipientLastName"
        flexDirection="row"
      />
      <TextfieldController
        name="infoRecipientPhone"
        label={t('patients:contactPhone')}
      />

      <FormControl error={!!errors.patientSigned}>
        <FormControlLabel
          control={
            <Checkbox
              {...register('patientSigned', { required })}
              color="primary"
            />
          }
          label={t('patients:patientSigned')}
        />
        <FormHelperText>
          {errors.patientSigned?.message as string}
        </FormHelperText>
      </FormControl>
    </>
  )
}
